import { MenuOutlined } from "@ant-design/icons"
import { COLOR } from "@finway-group/shared/lib/consts"
import { RightEnum, Workflow } from "@finway-group/shared/lib/models"
import { Button, Tag, Tooltip } from "antd"
import { ColumnsType } from "antd/lib/table"
import { TFunction } from "i18next"
import React from "react"
import { Edit as EditIcon, Trash2 as TrashIcon } from "react-feather"

import { AuthzService, WorkflowService } from "Shared/services"
import DialogService from "Shared/services/dialog.service"
import NotificationService, { NotificationTypeEnum } from "Shared/services/notification.service"
import { ThunkDispatchResult } from "Shared/store"
import { refetchTable } from "Shared/store/actions/tables/tableActions"
import { fetchAllWorkflows } from "Shared/store/actions/workflow/workflowActions"
import { TablesEnum } from "Shared/store/reducers/tableConfigReducer"
import { getTooltipPopupContainer } from "Shared/utils/helper.utils"
import { getRequestAndTriggerTypesOptions } from "Shared/utils/workflow.form.utils"

export const getWorkflowColumns = ({
    t,
    onEditWorkflow,
    isTravelEnabled,
    dispatch,
}: {
    t: TFunction
    onEditWorkflow: (id: string) => void
    isTravelEnabled?: boolean
    dispatch: ThunkDispatchResult
}): ColumnsType => {
    const userCanUpdateWorkflow = AuthzService.isRightGrantedForLoggedInUser(RightEnum.WORKFLOW__ALL__UPDATE)
    const userCanDeleteWorkflow = AuthzService.isRightGrantedForLoggedInUser(RightEnum.WORKFLOW__ALL__DELETE)

    const onDelete = async (id: string) => {
        const { checked, result } = await DialogService.confirmWorkflowDeletion(false)
        if (!result) {
            return
        }
        try {
            const deleteWorkflow = async (metadata: { recalculateApprovalProcessesInProgress?: boolean } = {}) => {
                try {
                    await WorkflowService.deleteWorkflow(id, metadata)
                    // refetch all rules in store
                    await dispatch(fetchAllWorkflows())
                    dispatch(refetchTable(TablesEnum.WORKFLOWS))
                    NotificationService.send(NotificationTypeEnum.SUCCESS, t("notification:workflow.deleted.title"), t("notification:workflow.deleted.message"))
                } catch (err) {
                    NotificationService.showErrorNotificationBasedOnResponseError(err, t("error:error"))
                }
            }
            if (checked) {
                const confirmRecalculateApprovalProcesses = await DialogService.confirmRecalculateApprovalProcesses()
                if (!confirmRecalculateApprovalProcesses) {
                    return
                }
            }

            await deleteWorkflow({ recalculateApprovalProcessesInProgress: checked })
        } catch (err) {
            NotificationService.showErrorNotificationBasedOnResponseError(err, t("error:error"))
        }
    }
    return [
        {
            key: "sort",
            dataIndex: "sort",
            title: "Order",
            width: 30,
            className: "drag-visible",
            render: () => {
                if (!userCanUpdateWorkflow) {
                    return null
                }
                return (
                    <div className="mb-4">
                        <MenuOutlined style={{ cursor: "grab", color: COLOR.gray[900] }} />
                    </div>
                )
            },
        },
        {
            key: "name",
            title: t("label:name"),
            render: (workflow: Workflow, _record) => (
                <span className="font-bold">
                    #{workflow?.order} {workflow.name}
                </span>
            ),
        },
        {
            key: "steps",
            title: t("label:steps"),
            render: (workflow: Workflow) => (
                <>
                    <Tag className="ant-tag ant-tag-green-highlight pb-2" color="primary">
                        {workflow.steps.length}
                    </Tag>
                    <span className="ml-8">{t("label:steps")}</span>
                </>
            ),
        },
        {
            key: "triggerType",
            title: t("label:workflow.trigger.trigger_type"),
            render: (workflow: Workflow) => {
                if (!workflow.triggerConditionExpression) {
                    return <></>
                }
                const { triggerTypes } = getRequestAndTriggerTypesOptions(workflow, isTravelEnabled ?? false)

                return (
                    <div className="flex flex-col">
                        <span>{t("label:workflow.trigger.trigger_type")}</span>
                        <div>
                            <span className="font-bold">
                                {triggerTypes.length === 1 ? (
                                    triggerTypes[0].condition?.toLowerCase() ? (
                                        t(`label:workflow.trigger.${triggerTypes[0].condition?.toLowerCase()}`)
                                    ) : (
                                        ""
                                    )
                                ) : (
                                    <>
                                        <Tooltip
                                            getPopupContainer={getTooltipPopupContainer}
                                            title={() => {
                                                const triggerTypesTooltipText = `${triggerTypes
                                                    .map((triggerType) => t(`label:workflow.trigger.${triggerType.condition?.toLowerCase()}`))
                                                    .join(", ")}.`
                                                return <p>{triggerTypesTooltipText}</p>
                                            }}
                                            placement="top"
                                        >
                                            <Tag className="text-base bg-gray-150">{t("label:workflow.types_amount", { amount: triggerTypes.length })}</Tag>
                                        </Tooltip>
                                    </>
                                )}
                            </span>
                        </div>
                    </div>
                )
            },
        },
        {
            key: "requestType",
            title: t("label:workflow.request_type.title"),
            render: (workflow: Workflow) => {
                if (!workflow.triggerConditionExpression) {
                    return <></>
                }
                const { requestTypes } = getRequestAndTriggerTypesOptions(workflow, isTravelEnabled ?? false)

                return (
                    <div className="flex flex-col">
                        <span>{t("label:workflow.request_type.title")}</span>
                        <span className="font-bold">
                            {requestTypes?.length === 1 ? (
                                t(`label:workflow.request_type.${requestTypes[0].toLowerCase()}`)
                            ) : (
                                <>
                                    <Tooltip
                                        getPopupContainer={getTooltipPopupContainer}
                                        title={() => {
                                            const requestTypeTooltipText = `${requestTypes
                                                .map((requestType) => t(`label:workflow.request_type.${requestType.toLowerCase()}`))
                                                .join(", ")}.`
                                            return <p>{requestTypeTooltipText}</p>
                                        }}
                                        placement="top"
                                    >
                                        <Tag className="text-base bg-gray-150">{t("label:workflow.types_amount", { amount: requestTypes.length })}</Tag>
                                    </Tooltip>
                                </>
                            )}
                        </span>
                    </div>
                )
            },
        },
        {
            key: "action",
            title: t("label:action"),
            width: 100,
            render: (workflow: Workflow) => (
                <>
                    {userCanUpdateWorkflow && (
                        <Button data-testid="finway:workflowTable:edit-btn" className="mr-4" onClick={() => onEditWorkflow(workflow._id)}>
                            <EditIcon />
                        </Button>
                    )}
                    {userCanDeleteWorkflow && (
                        <Button
                            data-testid="finway:workflowTable:delete-btn"
                            onClick={() => {
                                onDelete(workflow._id)
                            }}
                        >
                            <TrashIcon />
                        </Button>
                    )}
                </>
            ),
        },
    ]
}
